import React, { useEffect, useRef, useState } from "react";

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";
const TURNSTILE_SITE_KEY = "0x4AAAAAAE4ONfCyxBOVVG26";

type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileScript: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScript) return turnstileScript;

  turnstileScript = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existingScript || document.createElement("script");

    const onLoad = () => window.turnstile
      ? resolve(window.turnstile)
      : reject(new Error("Turnstile did not initialise."));
    const onError = () => reject(new Error("Turnstile could not be loaded."));

    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });

    if (!existingScript) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return turnstileScript;
}

interface TurnstileProps {
  onVerify: (token: string) => void;
  onError?: () => void;
  resetSignal?: number;
  className?: string;
}

/** A single explicit Turnstile widget for a public form submission. */
const Turnstile: React.FC<TurnstileProps> = ({
  onVerify,
  onError,
  resetSignal = 0,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>();
  const apiRef = useRef<TurnstileApi>();
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  const [api, setApi] = useState<TurnstileApi>();

  onVerifyRef.current = onVerify;
  onErrorRef.current = onError;

  useEffect(() => {
    let active = true;

    loadTurnstile()
      .then((turnstile) => {
        if (!active || !containerRef.current) return;

        apiRef.current = turnstile;
        setApi(turnstile);
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => onVerifyRef.current(token),
          "expired-callback": () => onVerifyRef.current(""),
          "error-callback": () => {
            onVerifyRef.current("");
            onErrorRef.current?.();
          },
          theme: "light",
        });
      })
      .catch(() => onErrorRef.current?.());

    return () => {
      active = false;
      if (apiRef.current && widgetIdRef.current) apiRef.current.remove(widgetIdRef.current);
    };
  }, []);

  useEffect(() => {
    if (resetSignal && api && widgetIdRef.current) api.reset(widgetIdRef.current);
  }, [api, resetSignal]);

  return <div className={`turnstile-widget ${className}`} ref={containerRef} />;
};

export default Turnstile;
